import { computed } from "vue";
import EmployeeSecurity from "@/enumerations/Security/EmployeeSecurity";
import ViewOnlyFullAccessSecurity from "@/enumerations/Security/ViewOnlyFullAccessSecurity";
import YesNoSecurity from "@/enumerations/Security/YesNoSecurity";
import { usePortalSecuritySettings } from "@/composables/Security/usePortalSecuritySettings";
import { useAuthStore } from "@/stores/auth";
import { useParametersStore } from "@/stores/parameters";

export function useAccountSecuritySettings() {
  const authStore = useAuthStore();
  const parametersStore = useParametersStore();
  const portalSecuritySettings = usePortalSecuritySettings();

  function checkUserProfileBasedSecurity(
    currentSecurityLevel: number | undefined,
    minimumSecurityLevel: number
  ) {
    if (
      authStore.webLogin.isEmployee &&
      authStore.webLogin.employeeSecurity === EmployeeSecurity.userProfile &&
      !authStore.selectedAccount?.isAccountLinkedToWebLogin
    ) {
      return (currentSecurityLevel ?? 0) >= minimumSecurityLevel;
    }

    return true;
  }

  const showAccount = computed(() => {
    return (
      !portalSecuritySettings.managerAccountSelected.value &&
      checkUserProfileBasedSecurity(
        authStore.userProfileBasedSecurity?.account,
        YesNoSecurity.yes
      )
    );
  });

  const showAccountSummaryPage = computed(() => {
    return (
      showAccount.value &&
      checkUserProfileBasedSecurity(
        authStore.userProfileBasedSecurity?.viewAccountSummary,
        YesNoSecurity.yes
      )
    );
  });

  const showAddressesPage = computed(() => {
    return (
      parametersStore.parameterSettings.showAddressBook &&
      showAccount.value &&
      checkUserProfileBasedSecurity(
        authStore.userProfileBasedSecurity?.addressBook,
        ViewOnlyFullAccessSecurity.viewOnly
      )
    );
  });

  const editAddressesPage = computed(() => {
    return (
      showAddressesPage.value &&
      !portalSecuritySettings.viewOnly.value &&
      checkUserProfileBasedSecurity(
        authStore.userProfileBasedSecurity?.addressBook,
        ViewOnlyFullAccessSecurity.fullAccess
      )
    );
  });

  const showBuyersPage = computed(() => {
    return (
      parametersStore.parameterSettings.showBuyers &&
      showAccount.value &&
      checkUserProfileBasedSecurity(
        authStore.userProfileBasedSecurity?.buyers,
        ViewOnlyFullAccessSecurity.viewOnly
      )
    );
  });

  const editBuyersPage = computed(() => {
    return (
      showBuyersPage.value &&
      !portalSecuritySettings.viewOnly.value &&
      checkUserProfileBasedSecurity(
        authStore.userProfileBasedSecurity?.buyers,
        ViewOnlyFullAccessSecurity.fullAccess
      )
    );
  });

  const showLoginHistoryPage = computed(() => {
    return (
      showAccount.value &&
      checkUserProfileBasedSecurity(
        authStore.userProfileBasedSecurity?.viewLoginHistory,
        YesNoSecurity.yes
      )
    );
  });

  const showProfilePage = computed(() => {
    return (
      showAccount.value &&
      checkUserProfileBasedSecurity(
        authStore.userProfileBasedSecurity?.accountProfile,
        ViewOnlyFullAccessSecurity.viewOnly
      )
    );
  });

  const editProfilePage = computed(() => {
    return (
      showProfilePage.value &&
      !portalSecuritySettings.viewOnly.value &&
      checkUserProfileBasedSecurity(
        authStore.userProfileBasedSecurity?.accountProfile,
        ViewOnlyFullAccessSecurity.fullAccess
      )
    );
  });

  const showPatronagePage = computed(() => {
    return (
      parametersStore.parameterSettings.showPatronage &&
      showAccount.value &&
      checkUserProfileBasedSecurity(
        authStore.userProfileBasedSecurity?.viewPatronage,
        YesNoSecurity.yes
      )
    );
  });

  /** An entity install answers this per entity, because divisions are set up per entity rather than company wide. */
  const showPatronageByDivision = computed(() => {
    return parametersStore.parameterSettings.useEntities
      ? parametersStore.entityParameterSettings.showPatronageByDivision
      : parametersStore.parameterSettings.showPatronageByDivision;
  });

  //Every page the Account section can hold has to be listed here, or a profile allowed only the ones that are
  //missing loses the whole section and cannot reach a page it is entitled to.
  const showAccountMenu = computed(() => {
    return (
      showAccount.value &&
      (showAccountSummaryPage.value ||
        showAddressesPage.value ||
        showBuyersPage.value ||
        showLoginHistoryPage.value ||
        showProfilePage.value ||
        showPatronagePage.value)
    );
  });

  return {
    editAddressesPage,
    editBuyersPage,
    editProfilePage,
    showAccount,
    showAccountMenu,
    showAccountSummaryPage,
    showAddressesPage,
    showBuyersPage,
    showLoginHistoryPage,
    showPatronageByDivision,
    showPatronagePage,
    showProfilePage,
  };
}
